import { useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Input,
  Modal,
  Space,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';

interface FeatureFlag {
  key: string;
  enabled: boolean;
  description: string | null;
  reason: string | null;
  updatedBy: string | null;
  updatedAt: string;
}

const SAFETY_CRITICAL = new Set(['moderation.whisper.enabled', 'moderation.rules.enabled']);

/** Human-friendly labels for known flags. Unknown flags fall back to the
 *  dotted key so a new flag still renders cleanly without a UI deploy. */
const LABEL: Record<string, string> = {
  'moderation.hive.enabled': 'Hive Moderation (audio + video classifier)',
  'moderation.whisper.enabled': 'OpenAI Whisper (transcript + SOS detection)',
  'moderation.rules.enabled': 'In-house rules layer (PII / SOS regex)',
  'moderation.on_device.enabled': 'On-device in-call detection — MASTER switch',
  'moderation.on_device.nudity': '  ↳ Nudity (ends call)',
  'moderation.on_device.sexual': '  ↳ Sexual / suggestive (ends call)',
  'moderation.on_device.violence': '  ↳ Violence (blur + review)',
  'moderation.on_device.weapons': '  ↳ Weapons (blur + review)',
  'moderation.on_device.drugs': '  ↳ Drugs (blur + review)',
  'rtc.livekit.enabled': 'Use LiveKit for video calls (instead of Agora)',
};

/** Extra copy for flags whose effect isn't obvious from the label alone. */
const HINT: Record<string, string> = {
  'rtc.livekit.enabled':
    'Switches the media transport for NEW calls only. Ringing, accept/reject and billing are ' +
    'identical on both providers. The provider is pinned when a call starts, so flipping this ' +
    'never disturbs a call already in progress — and turning it back off is an instant rollback. ' +
    'Note: on-device in-call detection currently runs on Agora only.',
};

export function FeatureFlagsPage() {
  // Toggling is a separate permission from viewing the flags.
  const canEdit = usePermissions().can('feature_flags.edit');
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [pendingFlag, setPendingFlag] = useState<{ flag: FeatureFlag; next: boolean } | null>(null);
  const [reason, setReason] = useState('');

  const flags = useQuery({
    queryKey: ['admin', 'feature-flags'],
    queryFn: async () => (await api.get<FeatureFlag[]>('/admin/feature-flags')).data,
    refetchInterval: 30_000,
  });

  const set = useMutation({
    mutationFn: async () => {
      if (!pendingFlag) return;
      await api.post(`/admin/feature-flags/${pendingFlag.flag.key}`, {
        enabled: pendingFlag.next,
        reason,
      });
    },
    onSuccess: () => {
      message.success('Flag updated. Production picks up the change within 60 seconds.');
      setPendingFlag(null);
      setReason('');
      qc.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
    onError: (e: unknown) => {
      message.error(
        (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Could not update flag',
      );
    },
  });

  // Surface the dangerous combo: both Hive and Whisper off means the
  // entire moderation pipeline is bypassed. Admins should know.
  const moderationFullyOff =
    flags.data &&
    !flags.data.find((f) => f.key === 'moderation.hive.enabled')?.enabled &&
    !flags.data.find((f) => f.key === 'moderation.whisper.enabled')?.enabled;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ color: 'white', margin: 0 }}>
        Feature switches
      </Typography.Title>

      <Alert
        type="info"
        showIcon
        message="Flips propagate within 60 seconds (Redis cache TTL). Every change is audit-logged with the actor + reason."
      />

      {moderationFullyOff ? (
        <Alert
          type="error"
          showIcon
          message="Moderation pipeline is OFF"
          description="Both Hive and Whisper are disabled. Calls are running without automated safety classification. Re-enable at least one before resuming normal traffic."
        />
      ) : null}

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {flags.data?.map((f) => {
          const isSafetyCritical = SAFETY_CRITICAL.has(f.key);
          return (
            <Card key={f.key} size="small">
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space style={{ width: '100%', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Space direction="vertical" size={2} style={{ flex: 1 }}>
                    <Space size={8}>
                      <Typography.Text strong>{LABEL[f.key] ?? f.key}</Typography.Text>
                      {isSafetyCritical ? <Tag color="error">SAFETY-CRITICAL</Tag> : null}
                      <Tag color={f.enabled ? 'success' : 'default'}>
                        {f.enabled ? 'ON' : 'OFF'}
                      </Tag>
                    </Space>
                    {f.description ?? HINT[f.key] ? (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {f.description ?? HINT[f.key]}
                      </Typography.Text>
                    ) : null}
                  </Space>
                  {/* Read-only for an operator without feature_flags.edit —
                      shown so they can still see the state they are auditing. */}
                  <Switch
                    checked={f.enabled}
                    disabled={!canEdit}
                    onChange={(next) => setPendingFlag({ flag: f, next })}
                  />
                </Space>
                <Space size={8} style={{ fontSize: 11, color: '#8B8B8B' }}>
                  <code style={{ fontSize: 11 }}>{f.key}</code>
                  {f.updatedBy ? (
                    <>
                      <span>·</span>
                      <span>
                        last changed by {f.updatedBy.slice(0, 8)} on{' '}
                        {new Date(f.updatedAt).toLocaleString()}
                      </span>
                    </>
                  ) : null}
                  {f.reason ? (
                    <>
                      <span>·</span>
                      <span>&ldquo;{f.reason}&rdquo;</span>
                    </>
                  ) : null}
                </Space>
              </Space>
            </Card>
          );
        })}
      </Space>

      <Modal
        title={
          pendingFlag
            ? `${pendingFlag.next ? 'Enable' : 'Disable'} ${LABEL[pendingFlag.flag.key] ?? pendingFlag.flag.key}`
            : 'Toggle flag'
        }
        open={!!pendingFlag}
        onCancel={() => {
          setPendingFlag(null);
          setReason('');
        }}
        onOk={() => set.mutate()}
        okText={pendingFlag?.next ? 'Enable' : 'Disable'}
        okButtonProps={{
          danger: !pendingFlag?.next,
          disabled: reason.trim().length < 10,
          loading: set.isPending,
        }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={12}>
          {pendingFlag && !pendingFlag.next && SAFETY_CRITICAL.has(pendingFlag.flag.key) ? (
            <Alert
              type="error"
              showIcon
              message="This is a safety-critical flag"
              description="Disabling it removes a layer of user safety. Be sure this is intentional and time-bounded — re-enable as soon as the underlying issue is resolved."
            />
          ) : null}
          <Typography.Text>
            Provide a reason (≥ 10 characters). It&apos;s written to the audit log alongside your
            admin id so anyone reviewing tomorrow knows why this changed.
          </Typography.Text>
          <Input.TextArea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Hive returning 503 from Mumbai region, paused while support investigates."
          />
        </Space>
      </Modal>
    </Space>
  );
}
